<template>
	<div class="flex-1">
		<div class="flex h-full w-full flex-col bg-white shadow-xl p-6">
			<div class="flex flex-col h-full">
				<div class="flex items-start justify-between mb-6">
					<h2
						id="slide-over-title"
						class="text-lg capitalize font-medium text-gray-900 py-0"
					>
						{{ $t("signup") }}
					</h2>
					<div class="ml-3 flex h-7 items-center">
						<button
							type="button"
							class="-m-2 p-2 text-gray-700"
							@click="emits('close')"
						>
							<span class="sr-only">{{ $t("Close panel") }}</span>
							<XMarkIcon class="w-6 h-6" />
						</button>
					</div>
				</div>

				<div class="flex-1 min-h-0">
					<form
						class="flex-1 min-h-0 flex flex-col gap-6"
						@submit.prevent="invokeSubmit"
					>
						<p>{{ $t("create_a_new_account") }}</p>

						<AccountSignUpPersonalFields
							v-model:email="formData.Email"
							v-model:first-name="formData.FirstName"
							v-model:last-name="formData.LastName"
							v-model:password="formData.Password"
							:validation="{
								Email: $v.Email,
								FirstName: $v.FirstName,
								LastName: $v.LastName,
								Password: $v.Password
							}"
						/>

						<AccountSignUpCompanyFields
							v-if="b2bAccess"
							v-model:name="formData.Company.Name"
							v-model:org-no="formData.Company.OrgNo"
							v-model:vat-no="formData.Company.VatNo"
							v-model:email="formData.Company.Email"
							v-model:phone="formData.Company.Phone"
							:validation="$v.Company"
						/>

						<AccountSignUpAddressFields
							v-model:line1="formData.InvoiceAddress.Line1"
							v-model:zip="formData.InvoiceAddress.Zip"
							v-model:city="formData.InvoiceAddress.City"
							v-model:country-id="formData.InvoiceAddress.CountryId"
							:heading="b2bAccess ? $t('account.company_address') : ''"
							:validation="$v.InvoiceAddress"
							:country-options="countryOptions"
						/>

						<div>
							<SharedCheckbox
								id="agree"
								v-model="formData.agree"
								:content="
									$t('agree_data_protection', [
										`<a class='underline' target='_blank' href='#'>${$t(
											'data_protection_information'
										)}</a>`,
										`<a class='underline' target='_blank' href='#'>${$t(
											'general_terms_and_conditions'
										)}</a>`
									])
								"
								:error="!!$v.agree.$error"
							/>
						</div>

						<button
							type="submit"
							class="flex capitalize items-center justify-center px-5 py-2 text-base font-medium text-white shadow-sm bg-gray-800 disabled:opacity-50 hover:bg-gray-900 transition ease-in-out duration-200"
							:disabled="loading"
						>
							{{ $t("create_account") }}
						</button>
					</form>
					<nuxt-link
						class="mt-5 block text-center cursor-pointer font-medium text-base underline"
						@click="openLogin"
						>{{ $t("or_log_in_to_your_account") }}
					</nuxt-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/solid";
import type { SharedModal } from "../Shared/SharedModal.vue";
import SharedCheckbox from "../Shared/SharedCheckbox.vue";

const emits = defineEmits<{
	(e: "success"): void;
	(e: "close"): void;
}>();

const modal = inject<SharedModal>("modal") as SharedModal;
const { formData, $v, loading, countryOptions, b2bAccess, submit } =
	useAccountSignUp();

const invokeSubmit = async () => {
	if (await submit()) emits("close");
};

const openLogin = () => {
	emits("close");
	setTimeout(() => {
		modal.open("AccountLoginForm", { position: "side" });
	}, 300);
};
</script>
